import { isDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import type { ReactElement } from 'react';
import { Toast } from 'react-bootstrap';

import '@/App/Growl/Growl.css';
import '../Growl.css';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';

import { useCurrentUserQuery } from '@/api/user';
import { GrowlError } from '@/App/Growl/shared/GrowlError';
import { GrowlItem } from '@/App/Growl/shared/GrowlItem';
import { getExecutionColorAndIcon } from '@/App/Growl/utilities';
import { Panel } from '@/components/ui/Panel';
import type { GrowlOption } from '@/store/growl/state';
import type { FlexiForwardDealExecution } from '@/store/growl/state/flexi-forward';

interface GrowlFlexiForwardProps {
  growlItem: FlexiForwardDealExecution;
  options?: GrowlOption;
}

export function GrowlFlexiForward({ growlItem, options }: GrowlFlexiForwardProps): ReactElement {
  const { data: currentUser } = useCurrentUserQuery();

  const { status: executionStatus, data } = growlItem;

  const accountName = isDefined(currentUser)
    ? currentUser.clients.find((client) => client.companyId === data.accountId)?.companyName
    : undefined;

  const isExternalUser = isDefined(currentUser) && currentUser.userType === 'Trader';
  const isInternalSales = isDefined(currentUser) && currentUser.userType === 'Internal Sales';

  const [color, icon] = getExecutionColorAndIcon(executionStatus);

  const premium = Number.parseFloat(data.premium);
  const premiumValue = Math.abs(premium);
  const premiumWay = premium >= 0 ? 'pay' : 'receive';
  const premiumCurrency = data.premiumCurrencyIndex === 1 ? data.currency1 : data.currency2;

  const { closeable = true } = options ?? {};

  return (
    <>
      <Toast.Header
        className={`bg-${color} text-${color} bg-opacity-10`}
        closeButton={closeable}
        data-e2e="growl-header"
      >
        <i className="icon icon-sm me-2">{icon}</i>
        <strong className="me-auto">
          <FormattedMessage id={`growl.status.${executionStatus}`} defaultMessage={executionStatus || 'Unknown'} />
        </strong>
      </Toast.Header>
      <Toast.Body>
        <div data-e2e="growl-message">
          <FormattedMessage
            id={`growl.FxFlexiForward.${data.way}.${isExternalUser ? 'externalUser' : 'internalUser'}.message`}
            values={{
              currencyPair: `${data.currency1}/${data.currency2}`,
              notional: data.notional,
              notionalCurrency: data.notionalCurrencyIndex === 1 ? data.currency1 : data.currency2,
              rate: data.dealRate,
            }}
          />
        </div>

        {growlItem.status === 'rejected' && (
          <GrowlError
            errorCode={growlItem.errorCode}
            multipassErrorCode={growlItem.multipassErrorCode}
            reason={growlItem.reason}
            sessionId={growlItem.sessionId}
          />
        )}

        <Panel labelId={'growl.FxFlexiForward.dealDetails'} data-e2e="growl-details-toggle-button">
          <HStack horizontalAlignment="full" className="border-bottom py-8px fs-12">
            <span className="text-secondary" data-e2e="growl-client-way">
              <FormattedMessage id={`growl.FxFlexiForward.premium.${premiumWay}`} values={{ isInternalSales }} />
            </span>

            <span className="text-truncate text-end" data-e2e="premium-message">
              <FormattedNumber value={premiumValue} /> {premiumCurrency}
            </span>
          </HStack>

          <GrowlItem labelId="growl.FxFlexiForward.rate" data-e2e="rate">
            {data.dealRate}
          </GrowlItem>

          <GrowlItem labelId="growl.FxFlexiForward.account" data-e2e="growl-account">
            {accountName}
          </GrowlItem>

          <GrowlItem labelId="growl.FxFlexiForward.tradeDate" data-e2e="tradeDate">
            <FormattedDate
              value={data.tradeDate}
              day="2-digit"
              month="short"
              year="numeric"
              hour="2-digit"
              minute="numeric"
              hour12
            />
          </GrowlItem>

          {growlItem.status !== 'rejected' && (
            <GrowlItem labelId="growl.FxFlexiForward.tradeId" data-e2e="tradeId-message">
              {growlItem.status === 'confirmed' ? (
                growlItem.tradeIds[0]
              ) : (
                <FormattedMessage id="growl.FxFlexiForward.tradeId.awaitingConfirmation" />
              )}
            </GrowlItem>
          )}
        </Panel>
      </Toast.Body>
    </>
  );
}
